import { useState, useEffect, useRef, useCallback } from 'react';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { PhoneCall, Loader2, CheckCircle2, AudioLines, AlertTriangle, Play, Clock } from 'lucide-react';

const STATUS_META = {
  'Calling': { cls: 'bg-sky-100 text-sky-700', bar: 'bg-sky-500', icon: PhoneCall },
  'Whisper Processing': { cls: 'bg-violet-100 text-violet-700', bar: 'bg-violet-500', icon: AudioLines },
  'Verified': { cls: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500', icon: CheckCircle2 },
  'Review Required': { cls: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500', icon: AlertTriangle },
};

function fmtDuration(sec) {
  if (sec == null || sec <= 0) return '0s';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
}

function elapsedOf(startedAt) {
  if (!startedAt) return 0;
  const t = Date.parse(startedAt);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, (Date.now() - t) / 1000);
}

const Stat = ({ label, value, color, testid }) => (
  <div className="rounded-lg border border-slate-100 bg-white px-3 py-2" data-testid={testid}>
    <div className={`text-xl font-bold ${color || 'text-slate-900'}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

export default function OscarVerifyProgress() {
  const [p, setP] = useState(null);
  const [starting, setStarting] = useState(false);
  const [, setTick] = useState(0);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try { setP(await portalApi.oscarVerifyProgress()); } catch (e) { /* silent during polling */ }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 3000);
    timerRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(timerRef.current); };
  }, [load]);

  const start = async () => {
    if (!window.confirm('Start Stage 1 verification? This will place real Twilio calls to the Oscar IVR (up to 4 concurrent) for every member that is not yet verified.')) return;
    setStarting(true);
    try {
      const r = await portalApi.oscarVerifyStart();
      setP(r);
      toast.success('Verification started — calling the Oscar IVR (4 concurrent).');
    } catch (e) {
      toast.error(e.message || 'Could not start verification');
    } finally { setStarting(false); }
  };

  if (!p) return <div className="flex items-center gap-2 text-slate-400 text-sm p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading verification status…</div>;

  const avg = p.avg_call_sec || 48;
  const concurrency = p.concurrency || 4;
  const done = p.completed_audits + p.failed;
  const overallPct = p.total_members ? Math.min(100, Math.round((done / p.total_members) * 100)) : 0;

  // Build fixed lanes (one per concurrency slot) so the UI always shows the configured concurrency.
  const actives = p.active_calls || [];
  const lanes = [];
  for (let i = 0; i < Math.max(concurrency, actives.length); i += 1) lanes.push(actives[i] || null);

  return (
    <div className="space-y-5" data-testid="oscar-verify-progress">
      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900 flex items-center gap-2"><PhoneCall className="h-4 w-4 text-sky-600" /> Stage 1 — Verify Oscar Balances</div>
            <div className="text-xs text-slate-500 mt-0.5">Up to {concurrency} concurrent Twilio calls · each audit ≈ {Math.round(avg)}s · Whisper verifies the recorded balance after each call.</div>
          </div>
          <div className="flex items-center gap-2">
            {p.running
              ? <Badge className="bg-emerald-100 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse inline-block" />Running</Badge>
              : <Badge className="bg-slate-100 text-slate-500">Idle</Badge>}
            <Button onClick={start} disabled={starting || !p.twilio_configured || p.total_members === 0} data-testid="verify-start-btn">
              {starting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Verify Oscar Balances
            </Button>
          </div>
        </div>

        {!p.twilio_configured && <div className="text-xs text-amber-600">Twilio is not configured — verification cannot place calls.</div>}
        {p.total_members === 0 && <div className="text-xs text-slate-400">Import a CSV of members first.</div>}

        {/* Overall progress */}
        <div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{done} of {p.total_members} members processed</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Est. remaining: <strong className="text-slate-700">{p.running || p.pending ? fmtDuration(p.eta_seconds) : '—'}</strong></span>
          </div>
          <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="verify-overall-bar">
            <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-[width] duration-700 ease-out" style={{ width: `${overallPct}%` }} />
          </div>
          <div className="text-right text-[11px] text-slate-400 mt-0.5">{overallPct}%</div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
          <Stat label="Total" value={p.total_members} testid="vstat-total" />
          <Stat label="Pending" value={p.pending} color="text-slate-600" testid="vstat-pending" />
          <Stat label="Active" value={p.active} color="text-sky-600" testid="vstat-active" />
          <Stat label="Completed" value={p.completed_audits} color="text-emerald-600" testid="vstat-completed" />
          <Stat label="Verified" value={p.verified} color="text-emerald-700" testid="vstat-verified" />
          <Stat label="Failed" value={p.failed} color="text-rose-600" testid="vstat-failed" />
          <Stat label="Review" value={p.review_required} color="text-amber-600" testid="vstat-review" />
        </div>
      </Card>

      {/* Concurrent call lanes */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="verify-lanes">
        {lanes.map((call, i) => {
          if (!call) {
            return (
              <Card key={`idle-${i}`} className="p-4 border-dashed opacity-70" data-testid={`verify-lane-${i}`}>
                <div className="text-xs font-medium text-slate-400 flex items-center gap-2"><PhoneCall className="h-3.5 w-3.5" /> Line {i + 1}</div>
                <div className="text-sm text-slate-300 mt-6 text-center">Idle</div>
                <div className="h-2 w-full rounded-full bg-slate-100 mt-6" />
              </Card>
            );
          }
          const meta = STATUS_META[call.status] || STATUS_META.Calling;
          const Icon = meta.icon;
          const el = elapsedOf(call.started_at);
          const over = el > avg;
          let pct;
          if (call.status === 'Verified') pct = 100;
          else if (call.status === 'Whisper Processing') pct = 96;
          else pct = Math.min(95, (el / avg) * 100);
          return (
            <Card key={call.call_sid || call.member_id || i} className="p-4 space-y-3" data-testid={`verify-lane-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-slate-400 flex items-center gap-1.5"><PhoneCall className="h-3.5 w-3.5" /> Line {i + 1}</div>
                <Badge className={meta.cls}><Icon className="h-3 w-3 mr-1" />{call.status}</Badge>
              </div>
              <div className="font-mono text-sm text-slate-800" data-testid={`verify-lane-member-${i}`}>{call.member_id}</div>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${meta.bar} ${call.status === 'Whisper Processing' || over ? 'animate-pulse' : ''} transition-[width] duration-1000 ease-linear`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>{fmtDuration(el)} elapsed</span>
                <span>{over && call.status === 'Calling' ? 'Processing…' : `${Math.round(pct)}%`}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
